/* eslint-disable camelcase */
import React, { useContext, useMemo } from 'react';
import type { ChannelPreviewUIComponentProps } from 'stream-chat-react';
import { getOtherParticipantsFullNames, formatTimeStr } from 'utils';
import { Tooltip, Text, theme, ChannelThumbnail } from 'components';
import styled from 'styled-components';
import {
  ChannelType,
  ContextualChatContext,
  RightSidePanelPageType,
} from 'contexts';
import { MessageResponse, Channel } from 'stream-chat';
import { CHANNEL_NAME_LIST_TRUNCATE_SIZE } from './constants';

const ChannelSearchMessageItem = (
  props: Partial<ChannelPreviewUIComponentProps> & {
    userId: string;
    textParts: string[];
    debouncedQuery: string;
    handleChannelSelect: (channel: Channel, messageId?: string) => void;
    message: MessageResponse;
  },
) => {
  const {
    channel,
    userId,
    textParts,
    message,
    debouncedQuery,
    handleChannelSelect,
  } = props;
  const {
    channel: activeChannel,
    setSelectedContactId,
    setRightSidePanelPage,
  } = useContext(ContextualChatContext);
  const onSelectChannel = () => {
    // Reset card details
    setSelectedContactId('');
    setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
    // Navigate to desired channel on the main modal container
    handleChannelSelect(channel, message.id);
  };
  const { name: channelName, type } = channel.data;

  const chatMembers = useMemo(
    () => getOtherParticipantsFullNames(channel, userId),
    [channel, userId],
  );

  return (
    <StyledChannelItemContainer
      $active={!debouncedQuery && channel.id === activeChannel?.id}
      onClick={onSelectChannel}
    >
      {!!message.created_at && (
        <StyledTimeText>{formatTimeStr(message.created_at)}</StyledTimeText>
      )}
      {/* <ChannelThumbnail channel={channel} /> */}
      <StyledChatDetails>
        {type === ChannelType.listing && (
          <StyledChannelName>{channelName}</StyledChannelName>
        )}
        <Tooltip
          placement="bottomLeft"
          title={chatMembers}
          trigger={
            // Only trigger if the members length creates ellipsis
            chatMembers.length > 29 ? 'hover' : ''
          }
        >
          <StyledChannelMembers
            $type={type}
            ellipsis={CHANNEL_NAME_LIST_TRUNCATE_SIZE}
          >
            {chatMembers}
          </StyledChannelMembers>
        </Tooltip>

        <StyledResultContainer>
          {message.user?.name && (
            <MessageSender>{message.user.name}: </MessageSender>
          )}
          {textParts.map(part => {
            const isHighlight =
              part.toLowerCase() === debouncedQuery.toLowerCase();
            return (
              <>
                {isHighlight ? (
                  <HighlightedText>{part}</HighlightedText>
                ) : (
                  <NonHighlightedText>{part}</NonHighlightedText>
                )}
              </>
            );
          })}
        </StyledResultContainer>
      </StyledChatDetails>
    </StyledChannelItemContainer>
  );
};

const StyledChannelItemContainer = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 14px;
  border: 1px solid #f0f0f0;
  gap: 10px;
  width: 100%;
  background-color: ${({ $active }) => ($active ? '#F0F7FF' : '#FFFFFF')};
  &:hover {
    cursor: pointer;
    background-color: #f0f7ff;
  }
`;

const StyledChatDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const StyledChannelName = styled(Text)`
  font-weight: 700;
  margin: 0;
  font-size: 15px;
`;

const StyledChannelMembers = styled(Text)<{ $type: string }>`
  font-weight: ${({ $type }) => ($type === ChannelType.listing ? 600 : 700)};
  margin: 0;
  font-size: ${({ $type }) =>
    $type === ChannelType.listing ? '12px' : '15px'};
`;

const StyledResultContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
`;

const HighlightedText = styled(Text)`
  font-weight: 600;
  background-color: #ffedbf;
  color: #000;
  white-space: pre;
`;

const NonHighlightedText = styled(Text)`
  white-space: pre;
`;

const MessageSender = styled(Text)`
  font-weight: 600;
  color: #000;
  white-space: pre;
`;

const StyledTimeText = styled(Text)`
  position: absolute;
  right: 14px;
  top: 14px;
  color: ${theme.getColor('textSecondary')};
  margin: 0;
  font-size: 12px;
`;

export default ChannelSearchMessageItem;
